import { Injectable, inject } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { CertificatesFormModel } from '../../../shared/dto/certificates-form.model';
import {
  DictionariesListModel,
  DictionaryItemModel,
  DictionaryPayload,
} from '../../../shared/dto/dictionary-item.model';
import { DictionariesService } from '../../../shared/services/dictionaries.service';
import { GroupedAutocompleteOptions } from '../../../shared/dto/grouped-autocomplete-options.model';
import {
  MediaFileEntityModel,
  MediaFileWithValidityEntityModel,
} from '../../../shared/dto/media-response.model';
import { Params } from '@angular/router';
import { EmployeesAllData, EmployeeModel, ListEmployeePayload } from '../models/employee.model';
import {
  EmployeeStepsModel,
  EmployeeStep,
  EmployeeStep1Model,
  EmployeeStep2Model,
  EmployeeStep8Model,
  EmployeeStep7Model,
  EmployeeStep6Model,
  EmployeeStep3Model,
  EmployeeStep5Model,
  EmployeeStep4Model,
} from '../models/employee-steps.model';
import {
  StandardMediaEntityModel,
  StudentCardMediaEntityModel,
} from '../models/media-entity.model';
import { EmployeesApiService } from './employees-api.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeesDataService {
  private employeesApiService = inject(EmployeesApiService);
  private dictionariesService = inject(DictionariesService);

  getData(entityId: string, params: Params = null): Observable<EmployeesAllData> {
    const employee$: Observable<EmployeeModel> = entityId
      ? this.employeesApiService.get(entityId)
      : of(null);
    const employees$: Observable<ListEmployeePayload> = params
      ? this.employeesApiService.getAll(params)
      : of(null);
    const languages$: Observable<DictionaryItemModel[]> = this.dictionariesService.getLanguages();
    const primaryLanguages$: Observable<DictionaryPayload> =
      this.dictionariesService.getPrimaryLanguages();

    const dictionariesList = [
      'countries',
      'documenttypes',
      'maritalstatuses',
      'statutes',
      'dependentpartners',
      'skilllevels',
      'shiftregimes',
      'allocationregimes',
      'salaryregimes',
    ];
    const dictionaries$: Observable<DictionariesListModel> =
      this.dictionariesService.getDictionaries(dictionariesList);

    return forkJoin([employee$, employees$, dictionaries$, languages$, primaryLanguages$]).pipe(
      map(([employee, employees, { dictionaries }, languages, primaryLanguages]) => ({
        employee,
        employees,
        dictionaries,
        languages,
        primaryLanguages,
        employeeStepsModel: this.splitModelToSteps(employee),
      }))
    );
  }

  getListData(params?: Params): Observable<Partial<EmployeesAllData>> {
    const employees$: Observable<ListEmployeePayload> = params
      ? this.employeesApiService.getAll(params)
      : of(null);

    return forkJoin([employees$]).pipe(
      map(([employees]: [ListEmployeePayload]) => ({
        employees,
      }))
    );
  }

  getSuggestionsList(term: string): Observable<GroupedAutocompleteOptions[]> {
    return this.employeesApiService.getSuggestions(term);
  }

  joinStepsToModel(data: EmployeeStepsModel): EmployeeModel {
    const step8 = {
      identityMedia: [],
      creditCardMedia: [],
      studentCardMedia: [],
      otherMedia: [],
      holidayCertificateMedia: [],
      internalAuditMedia: [],
      referenceDocumentMedia: [],
      taxDocumentMedia: [],
      sicknessDocumentMedia: [],
      startupDreamboxMedia: [],
      registrationFormMedia: [],
      certificateMedia: [],
      drivingLicenseMedia: [],
    };
    const model = this.createDtoFromMediaArray(
      {
        ...data.step1,
        ...data.step2,
        ...data.step3,
        ...data.step4,
        ...data.step5,
        ...data.step6,
        ...data.step7,
        ...step8,
      },
      data.step8
    );
    return this.updateFeModelToBeModel(model);
  }

  updateFeModelToBeModel(data: EmployeeModel): EmployeeModel {
    Object.keys(data).forEach(key => {
      if (typeof data[key] === 'string') {
        data[key] = data[key].trim();
      }
      if (data[key] === '') {
        data[key] = null;
      }
    });
    return data;
  }

  splitModelToSteps(data: EmployeeModel): EmployeeStepsModel {
    const step1Keys = [
      'uuid',
      'firstName',
      'lastName',
      'socialSecurityNumber',
      'gender',
      'dateOfBirth',
      'placeOfBirth',
      'residenceAddress',
      'hasCustomResidencyAddress',
      'countryOfBirthCode',
      'address',
      'countryOfOriginCode',
      'documentTypeCode',
      'documentValidTo',
      'threeTerValidTo',
      'threeTer',
      'isDraft',
      'profilePicture',
      'activeAllocations',
      'activeAllocations',
      'officeCode',
      'consultant',
    ];
    const step2Keys = [
      'mobileNumber',
      'homeNumber',
      'communicationLanguageCode',
      'email',
      'electronicDocuments',
      'eSigning',
      'emergencyContacts',
    ];
    const step3Keys = [
      'allocationRegimeCodes',
      'hoursPerWeek',
      'languageSkills',
      'minGrossHour',
      'minGrossMonth',
      'infoCompetences',
      'desiredOccupationalProfiles',
      'salaryRegimeCode',
      'shiftRegimeCodes',
      'statuteCodes',
      'occupationProfiles',
      'excludeJobs',
    ];
    const step4Keys = ['drivingLicenses', 'personalInfo', 'transports'];
    const step5Keys = ['experiences'];
    const step6Keys = ['educations', 'certifications'];
    const step7Keys = [
      'maritalStatusCode',
      'dependentPartnerCode',
      'dependentChildren',
      'taxesCode',
      'frontierWorker',
      'iban',
    ];

    const step1 = this.objectSubSet(data, step1Keys) as EmployeeStep1Model;
    const step2 = this.objectSubSet(data, step2Keys) as EmployeeStep2Model;
    const step3 = this.objectSubSet(data, step3Keys) as EmployeeStep3Model;
    const step4 = this.objectSubSet(data, step4Keys) as EmployeeStep4Model;
    const step5 = this.objectSubSet(data, step5Keys) as EmployeeStep5Model;
    const step6 = this.objectSubSet(data, step6Keys) as EmployeeStep6Model;
    const step7 = this.objectSubSet(data, step7Keys) as EmployeeStep7Model;
    const step8 = this.createMediaArrayFromDTO(data) as EmployeeStep8Model;

    return { step1, step2, step3, step4, step5, step6, step7, step8 };
  }

  private objectSubSet(source: EmployeeModel, keys: string[]): EmployeeStep {
    return keys.reduce((res, key): object => {
      res[key] = source[key];
      return res;
    }, {});
  }

  private createMediaArrayFromDTO(data: EmployeeModel): EmployeeStep8Model {
    const step8 = { mediaList: [] };

    this.createStandardMedia(
      data.identityMedia,
      step8.mediaList,
      'idMedia',
      'EMPLOYEE_ONBOARDING.STEP8.IDENTITY_CARD'
    );
    this.createStandardMedia(
      data.creditCardMedia,
      step8.mediaList,
      'creditCardMedia',
      'EMPLOYEE_ONBOARDING.STEP8.DEBIT_CARD'
    );
    this.createSimpleMedia(
      data.holidayCertificateMedia,
      step8.mediaList,
      'holidayCertificateMedia',
      'EMPLOYEE_ONBOARDING.STEP8.HOLIDAY_CERTIFICATE'
    );
    this.createSimpleMedia(
      data.internalAuditMedia,
      step8.mediaList,
      'internalAuditMedia',
      'EMPLOYEE_ONBOARDING.STEP8.INTERNAL_AUDIT'
    );
    this.createSimpleMedia(
      data.referenceDocumentMedia,
      step8.mediaList,
      'referenceDocumentMedia',
      'EMPLOYEE_ONBOARDING.STEP8.REFERENCE_DOCUMENT'
    );
    this.createSimpleMedia(
      data.taxDocumentMedia,
      step8.mediaList,
      'taxDocumentMedia',
      'EMPLOYEE_ONBOARDING.STEP8.TAX_DOCUMENT'
    );
    this.createSimpleMedia(
      data.sicknessDocumentMedia,
      step8.mediaList,
      'sicknessDocumentMedia',
      'EMPLOYEE_ONBOARDING.STEP8.SICKNESS_DOCUMENT'
    );
    this.createSimpleMedia(
      data.startupDreamboxMedia,
      step8.mediaList,
      'startupDreamboxMedia',
      'EMPLOYEE_ONBOARDING.STEP8.STARTUP_DREAMBOX',
      false
    );
    this.createSimpleMedia(
      data.registrationFormMedia,
      step8.mediaList,
      'registrationFormMedia',
      'EMPLOYEE_ONBOARDING.STEP8.REGISTRATION_FORM',
      false
    );

    this.createSimpleMedia(
      data.certificateMedia,
      step8.mediaList,
      'certificateMedia',
      'EMPLOYEE_ONBOARDING.STEP8.CERTIFICATE'
    );
    this.createSimpleMediaWithValidity(
      data.drivingLicenseMedia,
      step8.mediaList,
      'drivingLicenseMedia',
      'EMPLOYEE_ONBOARDING.STEP8.DRIVING_LICENSE',
      !data.drivingLicenses?.length
    );

    if (data.certifications.length) {
      this.createCertificationsMedia(
        data.certifications,
        step8.mediaList,
        'certifications',
        'EMPLOYEE_ONBOARDING.STEP8.CERTIFICATE'
      );
    }
    if (
      ['LABOUR_STUDENT', 'WHITE_COLLAR_STUDENT', 'STUDENT'].some(
        code => data.statuteCodes.indexOf(code) !== -1
      ) ||
      data.studentCardMedia.length
    ) {
      this.createStudentCardMedia(
        data.studentCardMedia,
        step8.mediaList,
        'studentMedia',
        'EMPLOYEE_ONBOARDING.STEP8.STUDENT_AT_WORK'
      );
    }
    if (data.otherMedia.length) {
      this.createStandardMedia(
        data.otherMedia,
        step8.mediaList,
        'otherMedia',
        'EMPLOYEE_ONBOARDING.STEP8.OTHER'
      );
    }
    return step8;
  }

  private createStandardMedia(
    data: StandardMediaEntityModel[] = [],
    result,
    typeCode: string,
    typeName: string
  ): void {
    if (data.length) {
      data.forEach((entity, index) => {
        result.push({
          typeCode: `${typeCode}.${index}`,
          typeName,
          entityName: `${index}`,
          mediaKey: entity.media.key || null,
          mediaName: entity.media.name || null,
          validUntil: entity.validUntil || null,
        });
      });
    } else {
      result.push({
        typeCode,
        typeName,
        entityName: '0',
        mediaKey: null,
        mediaName: null,
        validUntil: null,
      });
    }
  }

  private createSimpleMedia(
    data: MediaFileEntityModel[] = [],
    result,
    typeCode: string,
    typeName: string,
    onlyDisplay: boolean = true
  ): void {
    if (data.length) {
      data.forEach((entity, index) => {
        result.push({
          typeCode: `${typeCode}.${index}`,
          typeName,
          entityName: `${index}`,
          mediaKey: entity.key || null,
          mediaName: entity.name || null,
          validUntil: null,
        });
      });
    } else if (!onlyDisplay) {
      result.push({
        typeCode,
        typeName,
        entityName: '0',
        mediaKey: null,
        mediaName: null,
        validUntil: null,
      });
    }
  }

  private createSimpleMediaWithValidity(
    data: MediaFileWithValidityEntityModel[] = [],
    result,
    typeCode: string,
    typeName: string,
    onlyDisplay: boolean = true
  ): void {
    if (data.length) {
      data.forEach((entity, index) => {
        result.push({
          typeCode: `${typeCode}.${index}`,
          typeName,
          entityName: `${index}`,
          mediaKey: entity.key || null,
          mediaName: entity.name || null,
          validUntil: entity.validUntil || null,
        });
      });
    } else if (!onlyDisplay) {
      result.push({
        typeCode,
        typeName,
        entityName: '0',
        mediaKey: null,
        mediaName: null,
        validUntil: null,
      });
    }
  }

  private createStudentCardMedia(
    data: StudentCardMediaEntityModel[],
    result,
    typeCode: string,
    typeName: string
  ): void {
    if (data.length) {
      data.forEach((entity, index) => {
        result.push({
          typeCode: `${typeCode}.${index}`,
          typeName,
          entityName: `${index}`,
          mediaKey: entity.media.key || null,
          mediaName: entity.media.name || null,
          remainingBalance: entity.remainingBalance || null,
        });
      });
    } else {
      result.push({
        typeCode,
        typeName,
        entityName: '0',
        mediaKey: null,
        mediaName: null,
        remainingBalance: null,
      });
    }
  }

  private createCertificationsMedia(
    data: CertificatesFormModel[],
    result,
    typeCode: string,
    typeName: string
  ): void {
    data.forEach(entity => {
      if (entity.media?.length) {
        entity.media.forEach((mediaEntity, index) => {
          result.push({
            typeCode: `${typeCode}.${entity.name}.${index}`,
            typeName,
            entityName: `${entity.name}.${index}`,
            mediaKey: mediaEntity.key || null,
            mediaName: mediaEntity.name || null,
            validUntil: entity.validTo || null,
          });
        });
      } else {
        result.push({
          typeCode: `${typeCode}.${entity.name}.0`,
          typeName,
          entityName: `${entity.name}.0`,
          mediaKey: null,
          mediaName: null,
          validUntil: entity.validTo || null,
        });
      }
    });
  }

  createDtoFromMediaArray(data: EmployeeModel, step8: EmployeeStep8Model): EmployeeModel {
    data.drivingLicenseMedia.length = 0;
    data.certifications.forEach(certificate => (certificate.media.length = 0));
    step8.mediaList
      .filter(media => media.mediaKey)
      .forEach(media => {
        const type = media.typeCode.split('.')[0];
        if (type === 'idMedia') {
          data.identityMedia.push({
            media: { key: media.mediaKey, name: media.mediaName },
            validUntil: media.validUntil,
          });
        }
        if (type === 'creditCardMedia') {
          data.creditCardMedia.push({
            media: { key: media.mediaKey, name: media.mediaName },
            validUntil: media.validUntil,
          });
        }
        if (type === 'otherMedia') {
          data.otherMedia.push({
            media: { key: media.mediaKey, name: media.mediaName },
            validUntil: media.validUntil,
          });
        }
        if (type === 'holidayCertificateMedia') {
          data.holidayCertificateMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'internalAuditMedia') {
          data.internalAuditMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'referenceDocumentMedia') {
          data.referenceDocumentMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'taxDocumentMedia') {
          data.taxDocumentMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'startupDreamboxMedia') {
          data.startupDreamboxMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'registrationFormMedia') {
          data.registrationFormMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'sicknessDocumentMedia') {
          data.sicknessDocumentMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'certificateMedia') {
          data.certificateMedia.push({ key: media.mediaKey, name: media.mediaName });
        }
        if (type === 'drivingLicenseMedia') {
          data.drivingLicenseMedia.push({
            key: media.mediaKey,
            name: media.mediaName,
            validUntil: media.validUntil,
          });
        }
        if (type === 'studentMedia') {
          data.studentCardMedia.push({
            media: { key: media.mediaKey, name: media.mediaName },
            remainingBalance: media.remainingBalance,
          });
        }
        if (type === 'certifications') {
          const subType = media.typeCode.split('.')[1];
          const certificate = data.certifications.find(certificate => certificate.name === subType);
          if (certificate) {
            certificate.media.push({ key: media.mediaKey, name: media.mediaName });
          }
        }
      });

    return data;
  }
}
